// react
import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

// models
import {
  ClientDetails,
  FeesDetails,
  GdprDetails,
  ReferClientRequest,
  ReferralFormType,
} from '@karehero/models';

// components
import { FeesDescription } from '@/components/organisms/FeesDescription/FeesDescription';
import { GdprConsent } from '@/components/organisms/GdprConsent/GdprConsent';
import { SjpClientForm } from '@/components/organisms/SjpClientForm/SjpClientForm';

import { ReferralProcess } from '@/components/molecules/ReferralProcess/ReferralProcess';

// hooks
import { SetIsValid } from '@/hooks';

/**
 * InviteClientForm renders the account settings page.
 */
export const InviteClientForm = ({
  value,
  formType,
  onChange,
  setIsValid,
}: InviteClientFormProps) => {
  // states
  const [client, setClient] = useState<Partial<ClientDetails>>({
    firstName: value.referral?.firstName,
    lastName: value.referral?.lastName,
    email: value.referral?.email,
    phoneNumber: value.referral?.phone,
    nameOfPersonReceivingCare: value.referral?.personReceivingCare,
    contextAboutSituation: value.referral?.careSituationContext,
  });
  const [fees, setFees] = useState<Partial<FeesDetails>>({
    discountCode: value.referral?.discountCode,
    feeSelection: value.referral?.feePaid,
  });
  const [gdpr, setGdpr] = useState<Partial<GdprDetails>>({
    gdprSelections: value.referral?.gdprSelections,
  });

  // effects
  useEffect(() => {
    onChange({
      referrer: {},
      referral: {
        firstName: client.firstName,
        lastName: client.lastName,
        email: client.email,
        phone: client.phoneNumber,
        discountCode: fees.discountCode,
        feePaid: fees.feeSelection,
        personReceivingCare: client.nameOfPersonReceivingCare,
        careSituationContext: client.contextAboutSituation,
        gdprSelections: gdpr.gdprSelections,
      },
    });
  }, [client, fees, gdpr]);

  return (
    <Wrapper>
      <Page>
        <ReferralProcess formType={formType} />
        <SjpClientForm
          value={client}
          setValue={setClient}
          formType={formType}
          noText={true}
          setIsValid={setIsValid}
        />
        <FeesDescription
          formType={formType}
          value={fees}
          setValue={setFees}
          isNoTitle
        />
        <GdprConsent formType={formType} value={gdpr} setValue={setGdpr} />
      </Page>
    </Wrapper>
  );
};

// styles
const Page = styled.div`
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
`;

const Wrapper = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  ${(props) =>
    props.theme.breakpoint.sm(css`
      min-width: 560px;
    `)}
  ${(props) =>
    props.theme.breakpoint.md(css`
      min-width: 920px;
    `)}
  gap: ${(props) => props.theme.spacing[36]};
  padding: ${(props) => props.theme.spacing[20]};
`;

// types
interface InviteClientFormProps {
  formType: ReferralFormType;
  value: ReferClientRequest;
  onChange: (referral: ReferClientRequest) => void;
  setIsValid?: SetIsValid;
}

InviteClientForm.displayName = 'InviteClientForm';

export default InviteClientForm;
